import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Building2, MapPin, Users, LogOut, RefreshCw, Trash2, Eye,
  Sun, Moon, TicketCheck, Send, Paperclip, X, ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { getSocieties, createSociety } from '../../api/society.api';
import { raiseTicket, getMyTickets } from '../../api/ticket.api';
import client from '../../api/client';
import Logo   from '../../components/common/Logo';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import Modal  from '../../components/common/Modal';
import './UserDashboard.css';

// ── Status badge helper ───────────────────────────────────────────────────────
const statusBadge = (s) => {
  const map = {
    'Pending':     'badge-warning',
    'In Progress': 'badge-info',
    'Resolved':    'badge-success',
    'Closed':      'badge-subtle',
  };
  return map[s] || 'badge-subtle';
};

// ── Society Card ──────────────────────────────────────────────────────────────
const SocietyCard = ({ society, onOpen, onDelete }) => (
  <div className="society-card" onClick={() => onOpen(society)}>
    <div className="society-card-top">
      <div className="society-card-icon"><Building2 size={20} /></div>
      <div className="society-card-badges">
        {society.activeModules?.map(m => (
          <span key={m} className="badge badge-gold">
            {m === 'year_end_accounting' ? 'Year End' : 'Society Book'}
          </span>
        ))}
      </div>
    </div>
    <div className="society-card-name">{society.name}</div>
    {society.registrationNumber && <div className="society-card-meta">Reg: {society.registrationNumber}</div>}
    {society.address?.city && (
      <div className="society-card-meta">
        <MapPin size={11} /> {society.address.city}{society.address.state ? `, ${society.address.state}` : ''}
      </div>
    )}
    <div className="society-card-footer">
      <span className="text-xs text-muted" style={{ display:'flex', alignItems:'center', gap:'0.3rem' }}>
        <Users size={12} /> {society.memberCount ?? 0} members
      </span>
      <div style={{ display:'flex', gap:'0.35rem' }} onClick={e => e.stopPropagation()}>
        <Button size="xs" variant="ghost" icon={Eye} onClick={() => onOpen(society)}>Open</Button>
        <Button size="xs" variant="ghost" icon={Trash2} onClick={() => onDelete(society)} title="Request deletion" />
      </div>
    </div>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isImpersonating, stopImpersonation } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const fileRef = useRef(null);

  const [societies,    setSocieties]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [createModal,  setCreateModal]  = useState(false);
  const [formLoading,  setFormLoading]  = useState(false);

  // Ticket states
  const [ticketModal,  setTicketModal]  = useState(false);  // raise new ticket
  const [myTickets,    setMyTickets]    = useState(false);   // view my tickets
  const [tickets,      setTickets]      = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketForm,   setTicketForm]   = useState({ subject: '', description: '', attachment: '', attachName: '' });
  const [raising,      setRaising]      = useState(false);
  const [expandedTkt,  setExpandedTkt]  = useState(null);

  const [form, setForm] = useState({
    name: '', registrationNumber: '',
    address: { line1: '', city: '', state: '', pincode: '' },
  });

  const fetchSocieties = useCallback(async () => {
    setLoading(true);
    try { const res = await getSocieties(); setSocieties(res.data.societies); }
    catch { toast.error('Failed to load societies'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSocieties(); }, [fetchSocieties]);

  const loadMyTickets = async () => {
    setTicketsLoading(true);
    try { const r = await getMyTickets(); setTickets(r.data.data || []); }
    catch { toast.error('Failed to load tickets'); }
    finally { setTicketsLoading(false); }
  };

  const openMyTickets = () => { setMyTickets(true); loadMyTickets(); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Society name is required'); return; }
    setFormLoading(true);
    try {
      await createSociety(form);
      toast.success('Society created');
      setCreateModal(false);
      setForm({ name: '', registrationNumber: '', address: { line1: '', city: '', state: '', pincode: '' } });
      fetchSocieties();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (society) => {
    if (!window.confirm(`Send deletion request to admin for "${society.name}"?`)) return;
    try {
      await client.post('/deletion-requests', {
        type: 'society', targetId: society._id,
        targetName: society.name, societyId: society._id, reason: 'Requested by user',
      });
      toast.success('Deletion request sent to admin.');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  // ── Ticket: file attachment ──
  const handleAttach = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Attachment must be under 2 MB'); return; }
    const reader = new FileReader();
    reader.onload = ev => setTicketForm(f => ({ ...f, attachment: ev.target.result, attachName: file.name }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Ticket: submit ──
  const handleRaiseTicket = async () => {
    if (!ticketForm.subject.trim())     { toast.error('Subject is required');     return; }
    if (!ticketForm.description.trim()) { toast.error('Description is required'); return; }
    setRaising(true);
    try {
      const res = await raiseTicket({
        subject:     ticketForm.subject.trim(),
        description: ticketForm.description.trim(),
        attachment:  ticketForm.attachment || '',
      });
      toast.success(`Ticket ${res.data.data.ticketNumber} raised successfully!`);
      setTicketModal(false);
      setTicketForm({ subject: '', description: '', attachment: '', attachName: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to raise ticket'); }
    finally { setRaising(false); }
  };

  const setAddr = (f) => (e) => setForm(p => ({ ...p, address: { ...p.address, [f]: e.target.value } }));

  return (
    <div className="user-layout">
      {/* Navbar */}
      <header className="user-nav">
        <div className="user-nav-left"><Logo size="sm" /></div>
        <div className="user-nav-center">
          {isImpersonating && <div className="impersonate-pill"><Eye size={11} /> Impersonating: {user?.name}</div>}
        </div>
        <div className="user-nav-right">
          <span className="user-greeting">{user?.name}</span>
          {isImpersonating ? (
            <Button variant="subtle" size="sm" icon={LogOut} onClick={() => { stopImpersonation(); navigate('/admin'); }}>
              Back to Admin
            </Button>
          ) : (
            <>
              <button onClick={toggleTheme} title="Toggle theme"
                style={{ background:'var(--bg-raised)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-sm)', width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-muted)', marginRight:'0.5rem' }}>
                {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
              </button>
              <Button variant="subtle" size="sm" icon={LogOut} onClick={() => { logout(); navigate('/user/login'); }}>
                Logout
              </Button>
            </>
          )}
        </div>
      </header>

      <main className="user-main">
        

        {/* Page header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
          <div>
            <h1>My Societies</h1>
            <p className="text-sm text-muted" style={{ marginTop:'0.2rem' }}>{societies.length} of {user?.societyLimit ?? 5} societies</p>
          </div>
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
            <Button variant="ghost" size="sm" icon={RefreshCw} onClick={fetchSocieties} />
            {/* Tickets buttons — next to New Society as requested */}
            <Button variant="subtle" size="sm" icon={TicketCheck} onClick={openMyTickets}>My Tickets</Button>
            <Button variant="subtle" size="sm" icon={Send}        onClick={() => setTicketModal(true)}>Raise Ticket</Button>
            <Button size="sm" icon={Plus} onClick={() => setCreateModal(true)}>New Society</Button>
          </div>
        </div>

        {/* Societies grid */}
        {loading
          ? <div className="loading-center"><div className="spinner" /></div>
          : societies.length === 0
            ? (
              <div className="empty-state">
                <Building2 size={48} strokeWidth={1} />
                <h3>No societies yet</h3>
                <p>Create your first housing society to get started</p>
                <Button icon={Plus} onClick={() => setCreateModal(true)} className="mt-2">Create Society</Button>
              </div>
            )
            : (
              <div className="societies-grid">
                {societies.map(s => (
                  <SocietyCard key={s._id} society={s}
                    onOpen={soc => navigate(`/dashboard/society/${soc._id}`)}
                    onDelete={handleDelete} />
                ))}
              </div>
            )
        }
      </main>

      {/* ── Create Society Modal ── */}
      <Modal isOpen={createModal} onClose={() => setCreateModal(false)}
        title="Create New Society" subtitle="Add a housing society to your account" size="md"
        footer={
          <>
            <Button variant="subtle" onClick={() => setCreateModal(false)}>Cancel</Button>
            <Button loading={formLoading} icon={Plus} onClick={handleCreate}>Create Society</Button>
          </>
        }>
        <form onSubmit={handleCreate} className="mform">
          <Input label="Society Name" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Sunshine Apartments" required />
          <Input label="Registration Number" value={form.registrationNumber}
            onChange={e => setForm(f => ({ ...f, registrationNumber: e.target.value }))}
            placeholder="Optional" />
          <div className="mform-grid">
            <Input label="Address Line 1" value={form.address.line1} onChange={setAddr('line1')} placeholder="Street / Building" />
            <Input label="City"           value={form.address.city}  onChange={setAddr('city')}  placeholder="Mumbai" />
          </div>
          <div className="mform-grid">
            <Input label="State"   value={form.address.state}   onChange={setAddr('state')}   placeholder="Maharashtra" />
            <Input label="Pincode" value={form.address.pincode} onChange={setAddr('pincode')} placeholder="400001" />
          </div>
        </form>
      </Modal>

      {/* ── Raise Ticket Modal ── */}
      <Modal isOpen={ticketModal} onClose={() => setTicketModal(false)}
        title="Raise a Ticket"
        subtitle="Describe your issue and our team will respond shortly"
        size="md"
        footer={
          <>
            <Button variant="subtle" onClick={() => setTicketModal(false)}>Cancel</Button>
            <Button loading={raising} icon={Send} onClick={handleRaiseTicket}>Submit Ticket</Button>
          </>
        }>
        <div className="mform">
          <Input label="Subject *" value={ticketForm.subject}
            onChange={e => setTicketForm(f => ({ ...f, subject: e.target.value }))}
            placeholder="Brief summary of the issue" />

          <div className="field">
            <label className="field-label">Description *</label>
            <div className="field-control">
              <textarea className="field-input" rows={5}
                placeholder="Describe your issue in detail…"
                value={ticketForm.description}
                onChange={e => setTicketForm(f => ({ ...f, description: e.target.value }))}
                style={{ resize: 'vertical', minHeight: 120 }} />
            </div>
          </div>

          {/* Attachment */}
          <div className="field">
            <label className="field-label">Attachment <span style={{ color:'var(--text-dim)', fontWeight:400 }}>(optional, max 2 MB)</span></label>
            <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
              <label style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.45rem 0.9rem', background:'var(--bg-raised)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', cursor:'pointer', fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                <Paperclip size={13} />
                {ticketForm.attachName || 'Choose file'}
                <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx" style={{ display:'none' }} onChange={handleAttach} />
              </label>
              {ticketForm.attachName && (
                <button onClick={() => setTicketForm(f => ({ ...f, attachment: '', attachName: '' }))}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', display:'flex', alignItems:'center' }}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* ── My Tickets Modal ── */}
      <Modal isOpen={myTickets} onClose={() => setMyTickets(false)}
        title="My Tickets"
        subtitle="Track the status of your support tickets"
        size="lg"
        footer={
          <div style={{ display:'flex', gap:'0.75rem', justifyContent:'space-between', width:'100%', alignItems:'center' }}>
            <Button variant="ghost" size="sm" icon={Send} onClick={() => { setMyTickets(false); setTicketModal(true); }}>
              Raise New Ticket
            </Button>
            <Button variant="subtle" onClick={() => setMyTickets(false)}>Close</Button>
          </div>
        }>
        {ticketsLoading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : tickets.length === 0 ? (
          <div className="empty-state" style={{ padding:'2rem' }}>
            <TicketCheck size={36} strokeWidth={1} />
            <h3>No tickets yet</h3>
            <p>Raise a ticket if you have any issues</p>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.65rem' }}>
            {tickets.map(t => (
              <div key={t._id} style={{ background:'var(--bg-raised)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-sm)', overflow:'hidden' }}>
                {/* Ticket header */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.85rem 1rem', cursor:'pointer' }}
                  onClick={() => setExpandedTkt(expandedTkt === t._id ? null : t._id)}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flex:1, minWidth:0 }}>
                    <span style={{ fontFamily:'monospace', fontSize:'0.75rem', color:'var(--gold)', fontWeight:700, whiteSpace:'nowrap' }}>{t.ticketNumber}</span>
                    <span style={{ fontWeight:600, fontSize:'0.875rem', color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', flexShrink:0 }}>
                    <span className={`badge ${statusBadge(t.status)}`}>{t.status}</span>
                    <span style={{ fontSize:'0.75rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                      {new Date(t.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                    </span>
                    <ChevronDown size={14} color="var(--text-dim)"
                      style={{ transform: expandedTkt === t._id ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }} />
                  </div>
                </div>

                {/* Expanded detail */}
                {expandedTkt === t._id && (
                  <div style={{ borderTop:'1px solid var(--border-subtle)', padding:'1rem' }}>
                    <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:'0.75rem', lineHeight:1.6 }}>
                      {t.description}
                    </div>
                    {t.adminNote && (
                      <div style={{ padding:'0.65rem 0.85rem', background:'var(--info-bg)', borderRadius:'var(--radius-sm)', fontSize:'0.82rem', color:'var(--info)', borderLeft:'3px solid var(--info)' }}>
                        <strong>Admin Response:</strong> {t.adminNote}
                      </div>
                    )}
                    {/* Status history */}
                    {t.statusHistory?.length > 0 && (
                      <div style={{ marginTop:'0.75rem' }}>
                        <div style={{ fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--text-dim)', marginBottom:'0.4rem' }}>History</div>
                        {t.statusHistory.map((h, i) => (
                          <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.75rem', color:'var(--text-muted)', padding:'0.2rem 0' }}>
                            <span className={`badge ${statusBadge(h.status)}`} style={{ fontSize:'0.65rem' }}>{h.status}</span>
                            <span>{h.note || ''}</span>
                            <span style={{ marginLeft:'auto', color:'var(--text-dim)' }}>{new Date(h.changedAt).toLocaleDateString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserDashboard;
